import { useEffect, useSyncExternalStore } from 'react';
import type { Query, QueryClient } from '@tanstack/react-query';
import { isTripGenerating } from '../ai';
import { dayLegs, roadsQuery, tripDayStops } from '../maps/roads';
import type { TripEnvelope } from '../types';
import { STORE_TRIPS, idbClear, idbValues } from './db';
import {
  cacheMedia,
  deletePersistedQuery,
  isKeepingOffline,
  isTripKey,
  mediaUrlsOf,
  readMediaState,
  readPersistedQueries,
  requestPersistentStorage,
  writeMediaState,
  writePersistedQuery,
  type MediaState,
} from './store';

/**
 * Keeping opened trips on the device.
 *
 * Rather than a second model of "the offline trip", the query cache itself is
 * persisted: every successful answer to a query on the list below goes to
 * IndexedDB under the query's own key, and on start-up the stored answers are
 * put back into the cache before the app renders. A page then reads exactly
 * what it read online — the same component, the same link — and when the
 * network is gone, TanStack keeps the restored data and reports the failed
 * refetch, which the page shows as «копия с устройства».
 *
 * A trip's answer also starts two downloads, so that "opened once" means
 * "readable without a network": its photos and documents into the service
 * worker's media cache, and the roads the map draws between its stops.
 */

/** Which answers are kept. Everything else (AI, history, admin) needs a server anyway. */
function isPersisted(key: readonly unknown[]): boolean {
  if (isTripKey(key)) return true;
  if (key[0] === 'trip' && key[2] === 'flights') return true; // last known flight status
  if (key[0] === 'maps' && key[1] === 'roads') return true;
  if (key[0] === 'exchange-rates') return true;
  if (key[0] === 'trips' && key[1] === 'mine') return true; // the dashboard
  return false;
}

/** Roads are keyed by the legs shown, so day filters leave stray sets behind; they age out. */
const ROADS_MAX_AGE_MS = 30 * 24 * 60 * 60_000;

/**
 * Put stored answers back into the cache. Awaited before the first render —
 * a page that mounted first would already be showing a spinner, or an error,
 * for a trip the device holds.
 */
export async function restorePersistedQueries(queryClient: QueryClient): Promise<void> {
  // Restored queries have no observer until their page opens; the default
  // five-minute gc would drop them before a traveller gets round to it.
  for (const prefix of [['trip'], ['maps', 'roads'], ['exchange-rates'], ['trips', 'mine']]) {
    queryClient.setQueryDefaults(prefix, { gcTime: Infinity });
  }

  await migrateSnapshots();
  const entries = await readPersistedQueries();
  const now = Date.now();
  for (const entry of entries) {
    if (entry.key[0] === 'maps' && now - entry.updatedAt > ROADS_MAX_AGE_MS) {
      void deletePersistedQuery(entry.hash);
      continue;
    }
    // Something newer may already be there (a query that raced the restore).
    const existing = queryClient.getQueryState(entry.key);
    if (existing?.data !== undefined && existing.dataUpdatedAt >= entry.updatedAt) continue;
    queryClient.setQueryData(entry.key, entry.data, { updatedAt: entry.updatedAt });
    // A slow restore lost the race: the page already asked, failed, and showed
    // an error — which setting data has just cleared, so the page would show
    // the device's copy as if it were fresh, editors and all. Ask again; it
    // fails the same way, now as a refetch error over the restored copy.
    if (existing?.status === 'error') {
      void queryClient.refetchQueries({ queryKey: entry.key, exact: true });
    }
  }
}

/**
 * Trips saved by the old «Сохранить офлайн» button lived in their own store as
 * `{ envelope }`. They become the owner's trip query, so a trip saved before
 * this release still opens on the plane it was saved for.
 */
async function migrateSnapshots(): Promise<void> {
  const old = await idbValues<{ id: string; envelope: TripEnvelope; savedAt: string }>(STORE_TRIPS);
  if (!old.length) return;
  const present = new Set((await readPersistedQueries()).map((entry) => entry.hash));
  for (const snapshot of old) {
    const key = ['trip', snapshot.id];
    const hash = JSON.stringify(key);
    if (present.has(hash) || !snapshot.envelope) continue;
    await writePersistedQuery({
      hash,
      key,
      data: snapshot.envelope,
      updatedAt: Date.parse(snapshot.savedAt) || Date.now(),
    });
  }
  await idbClear(STORE_TRIPS);
}

/**
 * Write every successful answer on the list to the device, a moment after it
 * lands: a generating trip is polled every five seconds, and an optimistic
 * edit followed by the server's answer is one write, not two.
 */
export function persistQueries(queryClient: QueryClient): () => void {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();

  const write = (query: Query) => {
    timers.delete(query.queryHash);
    if (!isKeepingOffline()) return;
    const { data, dataUpdatedAt } = query.state;
    if (data === undefined) return;
    void writePersistedQuery({
      hash: query.queryHash,
      key: query.queryKey,
      data,
      updatedAt: dataUpdatedAt,
    });
    // A trip the AI is still building changes every few seconds; its files
    // and roads are fetched once it settles.
    if (isTripKey(query.queryKey) && !isTripGenerating((data as TripEnvelope).trip)) {
      const envelope = data as TripEnvelope;
      void keepTripMedia(envelope);
      void keepTripRoads(queryClient, envelope);
    }
  };

  // The app closed or put away inside that moment still writes what it got.
  const flush = () => {
    if (document.visibilityState !== 'hidden') return;
    for (const [hash, timer] of timers) {
      clearTimeout(timer);
      const query = queryClient.getQueryCache().get(hash);
      if (query) write(query);
    }
  };
  document.addEventListener('visibilitychange', flush);

  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated') return;
    const query = event.query;
    if (!isPersisted(query.queryKey)) return;
    // Deleted, or closed by its author: the device stops holding it.
    if (event.action.type === 'error' && (event.action.error as { status?: number }).status === 404) {
      clearTimeout(timers.get(query.queryHash));
      void deletePersistedQuery(query.queryHash);
      return;
    }
    if (event.action.type !== 'success') return;
    clearTimeout(timers.get(query.queryHash));
    timers.set(
      query.queryHash,
      setTimeout(() => write(query), 800),
    );
  });
  return () => {
    unsubscribe();
    document.removeEventListener('visibilitychange', flush);
  };
}

// ---------------------------------------------------------------------------
// Roads
// ---------------------------------------------------------------------------

/**
 * The roads the map tab draws with no filter on, asked for now — so the map
 * opened in the mountains has its lines. One request per trip, under the very
 * key the map uses, and none at all when that key is already held.
 */
async function keepTripRoads(queryClient: QueryClient, envelope: TripEnvelope): Promise<void> {
  const { trip } = envelope;
  const legs = dayLegs(tripDayStops(trip.days, trip.places));
  const { wanted, queryKey, queryFn } = roadsQuery(legs);
  if (!wanted.length) return;
  try {
    await queryClient.prefetchQuery({ queryKey, queryFn, staleTime: Infinity });
  } catch {
    // The map will ask again when it opens.
  }
}

// ---------------------------------------------------------------------------
// Media, with progress the trip page can show
// ---------------------------------------------------------------------------

export interface MediaProgress extends MediaState {
  /** Still downloading. */
  running: boolean;
}

const progress = new Map<string, MediaProgress>();
const listeners = new Set<() => void>();
/** The URL set last brought down per trip: an unchanged trip is not re-checked file by file. */
const lastDone = new Map<string, string>();

function publish(tripId: string, state: MediaProgress) {
  progress.set(tripId, state);
  for (const listener of listeners) listener();
}

async function keepTripMedia(envelope: TripEnvelope): Promise<void> {
  const tripId = envelope.trip.id;
  const urls = mediaUrlsOf(envelope);
  const signature = urls.join('\n');
  if (progress.get(tripId)?.running || lastDone.get(tripId) === signature) return;

  requestPersistentStorage();
  publish(tripId, { total: urls.length, stored: 0, savedAt: new Date().toISOString(), running: true });
  const stored = await cacheMedia(urls, (done, total, storedSoFar) => {
    // Re-render at most every few files: a 400-photo trip is 400 events.
    if (done === total || done % 5 === 0) {
      publish(tripId, { total, stored: storedSoFar, savedAt: new Date().toISOString(), running: true });
    }
  });
  const state = { total: urls.length, stored, savedAt: new Date().toISOString() };
  publish(tripId, { ...state, running: false });
  if (stored === urls.length) lastDone.set(tripId, signature);
  if (isKeepingOffline()) await writeMediaState(tripId, state);
}

/** How much of a trip's files are on the device — live while downloading, else from the last run. */
export function useTripMedia(tripId: string | undefined): MediaProgress | null {
  const state = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => (tripId ? (progress.get(tripId) ?? null) : null),
  );

  useEffect(() => {
    if (!tripId || progress.has(tripId)) return;
    void readMediaState(tripId).then((saved) => {
      if (saved && !progress.has(tripId)) publish(tripId, { ...saved, running: false });
    });
  }, [tripId]);

  return state;
}

/** Forget in-memory progress — after the device's copy was cleared. */
export function resetMediaProgress(): void {
  progress.clear();
  lastDone.clear();
  for (const listener of listeners) listener();
}
